<script setup lang="ts">
import { computed, nextTick, PropType, ref, watch } from 'vue';
import logoSrc from '@/assets/ms-teams-logo.png';
import { DgrCheckbox } from '@stockmarkteam/donguri-ui';
import SettingToggleCard from '@/components/common/atoms/setting-toggle-card.vue';
import { GroupMSTeamsSettings, GroupMSTeamsSettingsWithType } from '@/types';
import { validateWebhookUrl } from '@/utils/validators';

const modelValue = defineModel({
  type: Object as PropType<GroupMSTeamsSettingsWithType>,
});
defineProps({ title: String });

const isEnabledTeamsNotification = computed({
  get: () => modelValue.value?.settings?.enabled_ms_teams_notification ?? false,
  set: value => {
    updateSettings({ enabled_ms_teams_notification: value });
  },
});

const webhookUrl = computed({
  get: () => modelValue.value?.settings?.webhook_url ?? '',
  set: value => {
    updateSettings({ webhook_url: value });
  },
});

const isEnabledTeamActivityContent = computed({
  get: () => modelValue.value?.settings?.enabled_team_activity_content ?? false,
  set: value => {
    updateSettings({ enabled_team_activity_content: value });
  },
});

const isEnabledComment = computed({
  get: () => modelValue.value?.settings?.enabled_comment_content ?? false,
  set: value => {
    updateSettings({ enabled_comment_content: value });
  },
});

const isEnabledGroupMark = computed({
  get: () => modelValue.value?.settings?.enabled_group_mark_content ?? false,
  set: value => {
    updateSettings({ enabled_group_mark_content: value });
  },
});

const isValidWebhookUrl = computed(() => validateWebhookUrl(webhookUrl.value));

const validateCheckStatus = (
  settings: Omit<GroupMSTeamsSettings, 'webhook_url'>,
) => {
  return (
    settings.enabled_comment_content ||
    settings.enabled_group_mark_content ||
    settings.enabled_team_activity_content
  );
};

const isValidCheckStatus = computed(() => {
  if (!modelValue.value) return false;
  return validateCheckStatus(modelValue.value.settings);
});

const webhookUrlInput = ref<HTMLInputElement>();
watch(
  () => isEnabledTeamsNotification.value,
  () => {
    if (isEnabledTeamsNotification.value) {
      nextTick(() => {
        webhookUrlInput.value?.focus();
      });
    }
  },
);

const updateSettings = (newSettings: Partial<GroupMSTeamsSettings>) => {
  if (!modelValue.value) return;

  const settings = {
    ...modelValue.value.settings,
    ...newSettings,
  };
  const validWebhookUrl = validateWebhookUrl(settings.webhook_url);
  const validCheckStatus = validateCheckStatus(settings);
  modelValue.value.submittable =
    (validWebhookUrl && validCheckStatus) ||
    !settings.enabled_ms_teams_notification;
  modelValue.value.settings = settings;
};
</script>

<template>
  <div class="ms-teams-setting-container">
    <div class="ms-teams-settings-card">
      <div class="c-title-bold--m">Teams連携</div>
      <DgrCheckbox
        class="o-teams-toggle-card"
        v-model="isEnabledTeamsNotification"
      >
        <div class="checkbox-content-wrapper">
          <img :src="logoSrc" alt="Microsoft Teams" />
          <div class="title c-text-regular--m">{{ title }}</div>
          <div class="description c-text-regular--s">
            <slot />
          </div>
        </div>
      </DgrCheckbox>
      <div class="o-teams-contents">
        <div
          class="o-teams-webhook-title c-title-bold--m"
          :class="{ disabled: !isEnabledTeamsNotification }"
        >
          Web hook URL（接続するチャネル）
        </div>
        <div
          class="o-teams-webhook-error c-text-regular--m c-formBlock__text--error"
          v-if="isEnabledTeamsNotification && !isValidWebhookUrl"
        >
          Web Hook URLを入力してください。
        </div>
        <input
          class="o-teams-webhook-input c-textInput"
          type="text"
          ref="webhookUrlInput"
          v-model.trim="webhookUrl"
          :disabled="!isEnabledTeamsNotification"
        />
      </div>
    </div>
    <div class="ms-teams-settings-card">
      <div
        class="o-teams-contents-title c-title-bold--m"
        :class="{ disabled: !isEnabledTeamsNotification }"
      >
        配信するコンテンツ
      </div>
      <div
        class="o-teams-checkbox-error c-text-regular--m c-formBlock__text--error"
        v-if="isEnabledTeamsNotification && !isValidCheckStatus"
      >
        1つ以上選択してください。
      </div>
      <div class="ms-teams-settings-card">
        <SettingToggleCard
          title="先週の注目記事（毎週1回）"
          v-model="isEnabledTeamActivityContent"
          :disabled="!isEnabledTeamsNotification"
        >
          このグループにグループマークした記事をランキング形式で配信します。
        </SettingToggleCard>
        <SettingToggleCard
          title="グループマークした記事（随時）"
          v-model="isEnabledGroupMark"
          :disabled="!isEnabledTeamsNotification"
        >
          このグループにグループマークした記事を配信します。
        </SettingToggleCard>
        <SettingToggleCard
          title="コメントした記事（随時）"
          v-model="isEnabledComment"
          :disabled="!isEnabledTeamsNotification"
        >
          このグループにコメントした記事とコメント内容を配信します。
        </SettingToggleCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ms-teams-setting-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ms-teams-settings-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.o-teams-contents {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.o-teams-toggle-card {
  .checkbox-content-wrapper {
    display: grid;
    grid-template:
      'logo title' 22px
      'logo description' minmax(0, 1fr)
      / 36px 1fr;
    gap: 0 8px;
  }

  img {
    grid-area: logo;
    align-self: center;
    width: 36px;
    height: 36px;
  }

  .title {
    grid-area: title;
  }

  .description {
    grid-area: description;
    color: $color-gray600;
  }
}

.o-submit-button-wrap {
  display: flex;
  justify-content: flex-end;
  width: 616px;

  .o-submit-button {
    margin: 0;
    width: 120px;
    height: 32px;
  }
}

.o-teams-webhook-title,
.o-teams-contents-title {
  &.disabled {
    color: $color-gray600;
  }
}

.o-teams-webhook-input {
  width: 448px;
  outline: none;
  margin: 4px 0 0 0;
}

.o-teams-webhook-help-link {
  color: $color-green600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
